import { z } from 'zod';
import { RESOURCE_TITLE_MAX_LENGTH, visibilityTypeValues, } from '../types/models.js';
export { RESOURCE_TITLE_MAX_LENGTH };
/** Matches `resourcePostSchema.url` (`z.url()`), after trimming. */
const resourceUrlSchema = z.url();
export const isValidResourceUrl = (url) => {
    const trimmed = url?.trim() ?? '';
    if (!trimmed)
        return false;
    return resourceUrlSchema.safeParse(trimmed).success;
};
export const RESOURCE_KINDS = [
    'video',
    'file',
    'gallery',
    'audio',
    'link',
];
export const attachmentMediaKind = (att) => {
    const mime = att.meta?.mimetype ?? '';
    const type = att.type ?? '';
    if (type === 'image' || mime.startsWith('image/'))
        return 'image';
    if (type === 'video' || mime.startsWith('video/'))
        return 'video';
    if (type === 'audio' || mime.startsWith('audio/'))
        return 'audio';
    return 'file';
};
export const resourceHasRequiredMedia = (resource) => {
    if (resource.resourceKind === 'link') {
        return isValidResourceUrl(resource.url);
    }
    const need = resource.resourceKind === 'gallery'
        ? 'image'
        : resource.resourceKind === 'video'
            ? 'video'
            : resource.resourceKind === 'audio'
                ? 'audio'
                : 'file';
    return (resource.attachments ?? []).some((att) => attachmentMediaKind(att) === need);
};
export const RESOURCE_FORM_ISSUES = [
    'title',
    'titleLength',
    'url',
    'media',
    'audience',
    'group',
];
export const resourceFormIssues = (input) => {
    const issues = [];
    if (!input.title?.trim())
        issues.push('title');
    else if (input.title.length > RESOURCE_TITLE_MAX_LENGTH) {
        issues.push('titleLength');
    }
    if (input.resourceKind === 'link') {
        if (!isValidResourceUrl(input.url))
            issues.push('url');
    }
    else if (!resourceHasRequiredMedia(input)) {
        issues.push('media');
    }
    if (input.visibility === 'direct' && !input.audience?.length) {
        issues.push('audience');
    }
    if (input.visibility === 'group' && !input.groupId) {
        issues.push('group');
    }
    return issues;
};
const isVisibility = (value) => !!value && visibilityTypeValues.includes(value);
/**
 * Draft visibility the profile may actually post with. A group-only author
 * starts on Specific Group; a single postable group is preselected. Pass
 * `postableGroupIds: null` while memberships are still loading so an existing
 * group choice is not cleared.
 */
export const resourceVisibilityForCapabilities = (input) => {
    const current = isVisibility(input.visibility) ? input.visibility : 'local';
    const fallback = input.allowed[0];
    if (!fallback) {
        return { visibility: current, groupId: input.groupId ?? undefined };
    }
    const keep = input.allowed.includes(current) ? current : fallback;
    if (keep !== 'group')
        return { visibility: keep };
    if (input.postableGroupIds == null) {
        return { visibility: 'group', groupId: input.groupId ?? undefined };
    }
    const kept = input.groupId && input.postableGroupIds.includes(input.groupId)
        ? input.groupId
        : undefined;
    return {
        visibility: 'group',
        groupId: kept ??
            (input.postableGroupIds.length === 1
                ? input.postableGroupIds[0]
                : undefined),
    };
};
export const categoryTreeFromPaths = (paths) => {
    const root = [];
    for (const path of paths) {
        let level = root;
        const acc = [];
        for (const name of path) {
            const segment = name.trim();
            if (!segment)
                continue;
            acc.push(segment);
            let node = level.find((entry) => entry.name === segment);
            if (!node) {
                node = { name: segment, path: [...acc], children: [] };
                level.push(node);
            }
            level = node.children;
        }
    }
    return root;
};
export const pathStartsWith = (path, prefix) => prefix.length <= path.length &&
    prefix.every((segment, index) => path[index] === segment);
export const normalizeResourceTags = (tags) => {
    const seen = new Set();
    const out = [];
    for (const raw of tags ?? []) {
        const tag = raw.replace(/^#/, '').trim().toLowerCase();
        if (!tag || seen.has(tag))
            continue;
        seen.add(tag);
        out.push(tag);
    }
    return out;
};
